<template>
  <div class="gc-bom-bulk-update-step-configure">
    <!-- Metric cards row -->
    <div class="cfg-metrics">
      <div class="cfg-metric-card">
        <span class="cfg-metric-label">BOMs matched</span>
        <span class="cfg-metric-value">{{ localBomList.length }}</span>
      </div>
      <div class="cfg-metric-card">
        <span class="cfg-metric-label">Materials / processes</span>
        <span class="cfg-metric-value">{{ uniqueMatchedCodesCount }}</span>
      </div>
      <div class="cfg-metric-card">
        <span class="cfg-metric-label">Selected</span>
        <span
          :class="[
            'cfg-metric-value',
            includedCount === 0 ? 'cfg-val-zero' : 'cfg-val-selected',
          ]"
          >{{ includedCount }}</span
        >
      </div>
      <div class="cfg-metric-card">
        <span class="cfg-metric-label">Excluded</span>
        <span
          :class="[
            'cfg-metric-value',
            excludedCount > 0 ? 'cfg-val-excluded' : '',
          ]"
          >{{ excludedCount }}</span
        >
      </div>
    </div>

    <!-- Zero-BOM guard -->
    <div
      v-if="includedCount === 0 && localBomList.length > 0"
      class="cfg-zero-bom"
    >
      All BOMs have been excluded. Include at least one BOM to continue.
    </div>

    <!-- BOM results section -->
    <div class="cfg-bom-section">
      <div class="cfg-bom-section-hdr">
        <span class="cfg-bom-section-title">BOM results — select scope</span>
        <div v-if="!isReadOnly" class="cfg-bom-section-actions">
          <button class="cfg-scope-btn" @click="selectAll">Select all</button>
          <button class="cfg-scope-btn" @click="clearAll">Clear all</button>
        </div>
      </div>

      <DxDataGrid
        :data-source="localBomList"
        :show-borders="false"
        :column-auto-width="false"
        :word-wrap-enabled="false"
        :hover-state-enabled="true"
        :row-alternation-enabled="false"
        :show-column-lines="false"
        :show-row-lines="true"
        key-expr="BomCode"
        class="cfg-bom-grid"
        :column-chooser="{ enabled: false }"
        :selected-row-keys="selectedRowKeys"
        @selection-changed="onSelectionChanged"
      >
        <DxSelection mode="multiple" show-check-boxes-mode="always" />
        <DxSearchPanel :visible="false" />
        <DxColumn data-field="BomCode" caption="BOM ID" :width="160" />
        <DxColumn
          data-field="ProductName"
          caption="Product"
          :minWidth="getMinWidthForGrid('ProductDescription').minWidth"
        />
        <DxColumn
          caption="Matched"
          cell-template="matched-cell"
          :width="300"
          :allow-sorting="false"
        />
        <DxColumn
          caption="Status"
          cell-template="status-cell"
          :width="110"
          :allow-sorting="false"
        />
        <DxColumn
          data-field="DateChanged"
          caption="Last Updated"
          data-type="date"
          :format="'dd MMM yyyy'"
          :minWidth="getMinWidthForGrid('Date').minWidth"
          :width="getMinWidthForGrid('Date').width"
        />
        <DxPaging :page-size="20" />
        <DxPager
          :show-page-size-selector="true"
          :allowed-page-sizes="[10, 20, 50]"
          :show-info="true"
        />

        <!-- Matched pills -->
        <template #matched-cell="{ data }">
          <div class="matched-pills">
            <span
              v-for="pill in getMatchedPills(data.data)"
              :key="pill.key"
              class="matched-pill"
              >{{ pill.label }}</span
            >
          </div>
        </template>

        <!-- Status badge -->
        <template #status-cell="{ data }">
          <GcInactive :inactive="data.data.BomStatus === 'X'" />
        </template>
      </DxDataGrid>

      <div class="cfg-bom-footer">
        Showing {{ localBomList.length }} matched BOMs
      </div>
    </div>

    <!-- Material config panel -->
    <GcBomBulkUpdateMaterialConfig
      v-if="hasMaterialCriteria"
      :value="localMaterialUpdateConfig"
      :matched-bom-list="matchedBomList"
      :is-read-only="isReadOnly"
      ref="materialConfig"
      @config-change="onMaterialConfigChange"
      @validation-change="onMaterialValidationChange"
    />

    <!-- Process config panel — process above material (Obs 14) -->
    <GcBomBulkUpdateProcessConfig
      v-if="hasProcessCriteria"
      :value="localProcessUpdateConfig"
      :matched-bom-list="matchedBomList"
      :is-read-only="isReadOnly"
      ref="processConfig"
      @config-change="onProcessConfigChange"
      @validation-change="onProcessValidationChange"
    />
  </div>
</template>

<script>
import DxDataGrid, {
  DxColumn,
  DxPaging,
  DxPager,
  DxSearchPanel,
  DxSelection,
} from "devextreme-vue/data-grid";
import GcBomBulkUpdateMaterialConfig from "@/components/production/bom-bulk-update/gc-bom-bulk-update-material-config";
import GcBomBulkUpdateProcessConfig from "@/components/production/bom-bulk-update/gc-bom-bulk-update-process-config";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";

export default {
  name: "GcBomBulkUpdateStepConfigure",
  components: {
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxSearchPanel,
    DxSelection,
    GcBomBulkUpdateMaterialConfig,
    GcBomBulkUpdateProcessConfig,
  },
  props: {
    matchedBomList: { type: Array, default: () => [] },
    excludedBomIds: { type: Array, default: () => [] },
    materialUpdateConfig: { type: Object, default: null },
    processUpdateConfig: { type: Object, default: null },
    hasMaterialCriteria: { type: Boolean, default: false },
    hasProcessCriteria: { type: Boolean, default: false },
    isReadOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      localBomList: [],
      localExcludedIds: [],
      localMaterialUpdateConfig: null,
      localProcessUpdateConfig: null,
      materialConfigValid: false,
      processConfigValid: false,
    };
  },
  computed: {
    includedCount() {
      return this.localBomList.filter(
        (b) => !this.localExcludedIds.includes(b.BomCode),
      ).length;
    },
    excludedCount() {
      return this.localBomList.length - this.includedCount;
    },
    selectedRowKeys() {
      return this.localBomList
        .map((b) => b.BomCode)
        .filter((code) => !this.localExcludedIds.includes(code));
    },
    isStepValid() {
      return this.materialConfigValid || this.processConfigValid;
    },
    uniqueMatchedCodesCount() {
      const codes = new Set();
      for (const bom of this.localBomList) {
        const mats = bom.MatchedMaterials || bom.matchedMaterials || [];
        for (const m of mats) {
          const code = m.MaterialCode || m.materialCode || m.Code || m.code;
          if (code) codes.add(code);
        }
        const procs = bom.MatchedProcesses || bom.matchedProcesses || [];
        for (const p of procs) {
          const code = p.ProcessCode || p.processCode || p.Code || p.code;
          if (code) codes.add(code);
        }
      }
      return codes.size;
    },
  },
  watch: {
    matchedBomList(newVal) {
      this.localBomList = newVal ? [...newVal] : [];
    },
    excludedBomIds(newVal) {
      if (JSON.stringify(newVal) !== JSON.stringify(this.localExcludedIds)) {
        this.localExcludedIds = newVal ? [...newVal] : [];
      }
    },
    materialUpdateConfig(newVal) {
      if (
        JSON.stringify(newVal) !==
        JSON.stringify(this.localMaterialUpdateConfig)
      ) {
        this.localMaterialUpdateConfig = newVal
          ? JSON.parse(JSON.stringify(newVal))
          : null;
      }
    },
    processUpdateConfig(newVal) {
      if (
        JSON.stringify(newVal) !== JSON.stringify(this.localProcessUpdateConfig)
      ) {
        this.localProcessUpdateConfig = newVal
          ? JSON.parse(JSON.stringify(newVal))
          : null;
      }
    },
  },
  created() {
    this.localBomList = this.matchedBomList ? [...this.matchedBomList] : [];
    this.localExcludedIds = this.excludedBomIds ? [...this.excludedBomIds] : [];
    this.localMaterialUpdateConfig = this.materialUpdateConfig
      ? JSON.parse(JSON.stringify(this.materialUpdateConfig))
      : null;
    this.localProcessUpdateConfig = this.processUpdateConfig
      ? JSON.parse(JSON.stringify(this.processUpdateConfig))
      : null;
  },
  methods: {
    getMinWidthForGrid(column) {
      return ColumnWidth[column];
    },
    getMatchedPills(bom) {
      const pills = [];
      const mats = bom.MatchedMaterials || bom.matchedMaterials || [];
      for (const m of mats) {
        const code = m.MaterialCode || m.materialCode || m.Code || m.code;
        const qty = m.Qty != null ? m.Qty : m.qty;
        if (code) {
          pills.push({
            key: `mat-${code}`,
            label: qty != null ? `${code} \u00d7 ${qty}` : code,
          });
        }
      }
      const procs = bom.MatchedProcesses || bom.matchedProcesses || [];
      for (const p of procs) {
        const code = p.ProcessCode || p.processCode || p.Code || p.code;
        const rate = p.RunRate != null ? p.RunRate : p.runRate;
        if (code) {
          pills.push({
            key: `proc-${code}`,
            label: rate != null ? `${code} \u00d7 ${rate}` : code,
          });
        }
      }
      return pills;
    },
    statusBadgeClass(status) {
      const s = (status || "").trim();
      if (s === "A" || s.toLowerCase() === "active") return "badge-active";
      if (s === "D" || s.toLowerCase() === "draft") return "badge-draft";
      if (s === "X" || s.toLowerCase() === "archived") return "badge-archived";
      if (s === "I" || s.toLowerCase() === "inactive") return "badge-archived";
      return "badge-default";
    },
    statusLabel(status) {
      const s = (status || "").trim();
      const map = { A: "Active", D: "Draft", X: "Archived", I: "Inactive" };
      return map[s] || s;
    },
    onSelectionChanged(e) {
      const allKeys = this.localBomList.map((b) => b.BomCode);
      this.localExcludedIds = allKeys.filter(
        (k) => !e.selectedRowKeys.includes(k),
      );
      this.emitConfigUpdate();
    },
    selectAll() {
      this.localExcludedIds = [];
      this.emitConfigUpdate();
    },
    clearAll() {
      this.localExcludedIds = this.localBomList.map((b) => b.BomCode);
      this.emitConfigUpdate();
    },
    onMaterialConfigChange(config) {
      this.localMaterialUpdateConfig = config;
      this.emitConfigUpdate();
    },
    onProcessConfigChange(config) {
      this.localProcessUpdateConfig = config;
      this.emitConfigUpdate();
    },
    onMaterialValidationChange({ isValid }) {
      this.materialConfigValid = isValid;
      this.$emit("validation-change", { isValid: this.isStepValid });
    },
    onProcessValidationChange({ isValid }) {
      this.processConfigValid = isValid;
      this.$emit("validation-change", { isValid: this.isStepValid });
    },
    emitConfigUpdate() {
      this.$emit("config-update", {
        excludedBomIds: [...this.localExcludedIds],
        materialUpdateConfig: this.localMaterialUpdateConfig,
        processUpdateConfig: this.localProcessUpdateConfig,
      });
    },
    validate() {
      let isValid = true;
      const bothShown = this.hasMaterialCriteria && this.hasProcessCriteria;
      if (this.hasMaterialCriteria && this.$refs.materialConfig) {
        if (!bothShown || this.materialConfigValid) {
          if (!this.$refs.materialConfig.validate()) isValid = false;
        }
      }
      if (this.hasProcessCriteria && this.$refs.processConfig) {
        if (!bothShown || this.processConfigValid) {
          if (!this.$refs.processConfig.validate()) isValid = false;
        }
      }
      return isValid;
    },
  },
};
</script>

<style lang="scss">
.gc-bom-bulk-update-step-configure {
  display: flex;
  flex-direction: column;
  gap: 10px;

  // ── Metric cards ───────────────────────────────────────────────────────────
  .cfg-metrics {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }

  .cfg-metric-card {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 18px;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 8px;
  }

  .cfg-metric-label {
    font-size: 12px;
    font-weight: 500;
    color: #6c757d;
  }

  .cfg-metric-value {
    font-size: 28px;
    font-weight: 700;
    color: #101828;
    line-height: 1;

    &.cfg-val-selected {
      color: #03a9f4;
    }

    &.cfg-val-excluded {
      color: #dc3545;
    }

    &.cfg-val-zero {
      color: #dc3545;
    }
  }

  // ── Zero-BOM guard ─────────────────────────────────────────────────────────
  .cfg-zero-bom {
    padding: 10px 14px;
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 6px;
    font-size: 13px;
    color: #856404;
  }

  // ── BOM results section ────────────────────────────────────────────────────
  .cfg-bom-section {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    overflow: hidden;
  }

  .cfg-bom-section-hdr {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid #dee2e6;
  }

  .cfg-bom-section-title {
    font-size: 14px;
    font-weight: 600;
    color: #101828;
  }

  .cfg-bom-section-actions {
    display: flex;
    gap: 8px;
  }

  .cfg-scope-btn {
    display: inline-flex;
    align-items: center;
    height: 32px;
    padding: 0 14px;
    background: #fff;
    border: 1px solid #d0d5dd;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #344054;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s, border-color 0.15s;

    &:hover {
      background: #f9fafb;
      border-color: #adb5bd;
    }

    &:active {
      background: #f2f4f7;
    }
  }

  // ── BOM grid ───────────────────────────────────────────────────────────────
  .cfg-bom-grid {
    // Remove DevExtreme default borders/backgrounds
    // .dx-datagrid-headers {
    //   background: #f9fafb;
    //   border-bottom: 1px solid #dee2e6;

    //   .dx-datagrid-text-content {
    //     font-size: 12px;
    //     font-weight: 600;
    //     color: #6c757d;
    //     text-transform: uppercase;
    //     letter-spacing: 0.4px;
    //   }
    // }

    // .dx-row td {
    //   vertical-align: middle;
    //   font-size: 13px;
    //   color: #344054;
    //   border-bottom: 1px solid #f2f4f7;
    // }

    // .dx-row:last-child td {
    //   border-bottom: none;
    // }
  }

  // ── Matched pills ──────────────────────────────────────────────────────────
  .matched-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 2px 0;
  }

  .matched-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    background: #e0f2fe;
    color: #0369a1;
    font-size: 12px;
    font-weight: 500;
    border-radius: 9999px;
    white-space: nowrap;
  }

  // ── Status badges ──────────────────────────────────────────────────────────
  .bom-status-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 500;

    &.badge-active {
      background: #d1fae5;
      color: #065f46;
    }

    &.badge-draft {
      background: #fef3c7;
      color: #92400e;
    }

    &.badge-archived,
    &.badge-default {
      background: transparent;
      color: #9ca3af;
    }
  }

  // ── BOM footer ─────────────────────────────────────────────────────────────
  .cfg-bom-footer {
    padding: 10px 18px;
    font-size: 12px;
    color: #6c757d;
    border-top: 1px solid #f2f4f7;
  }
}
</style>
